// notificationTest.js — how the notification self-test's result is READ.
//
// The server decides what happened and says so in words (`verdict.message`);
// this file only decides how it is coloured and how each channel row reads. The
// split matters for the same reason it does in streamCheck.js: the UI must not
// re-derive the diagnosis, or the two can disagree and the screen becomes one
// more thing to distrust.
//
// Pure — no React, no api client — so the rules are unit-testable.

/**
 * How long the client waits for POST /api/notifications/test-send.
 *
 * THE DEFAULT BUDGET IS SHORTER THAN THE SERVER'S OWN DEADLINE, WHICH MAKES A
 * WORKING SEND UNREPORTABLE. apiCore's default is 10s; the route deadlines each
 * channel at CHANNEL_TIMEOUT_MS (12s) and runs all three concurrently, so its
 * worst legitimate case is ~12s plus the user lookup and the pref read. A real
 * ACS email is `beginSend` followed by `pollUntilDone` — seconds, not
 * milliseconds — so the moment email actually started working the panel began
 * answering "Request timed out after 10s" over a send that was in fact fine.
 * Exactly the Sage-import failure (see SageImport.jsx): the request's own error
 * reported as the outcome of work that had already succeeded server-side.
 *
 * 30s sits above the server's worst case with room to spare and below the
 * Static Web Apps edge's ~45s cut-off, past which there is no response to wait
 * for anyway. It is NOT a retry budget — apiCore deliberately never retries a
 * timeout, since the aborted request may still be sending, and a retried test
 * would put a second email and a second SMS on the wire.
 *
 * Pinned against the server's CHANNEL_TIMEOUT_MS by test.
 */
export const TEST_SEND_TIMEOUT_MS = 30_000;

/**
 * Colour key per verdict kind, so the UI doesn't re-decide what a kind means.
 *
 * 'suppressed' is deliberately 'warn' and NOT 'good': the transport worked, so
 * every channel row is green, and the person still receives nothing because
 * they have email switched off. Colouring that success would hide the only
 * finding on the screen.
 */
export const VERDICT_TONE = {
  sent: 'good',
  'dry-run': 'warn',
  suppressed: 'warn',
  unconfigured: 'bad',
  failed: 'bad',
  unknown: 'warn',
};

/** Tone for a kind we have never seen — a newer API than this bundle. */
export const DEFAULT_TONE = 'warn';

export function toneFor(kind) {
  return VERDICT_TONE[kind] || DEFAULT_TONE;
}

/**
 * One channel's row, as label + tone. FOUR states, never three: sent, dry-run,
 * skipped (never attempted, with a reason) and failed. Folding `skipped` into
 * either neighbour is what made the old panel unreadable — "you never
 * registered with the Teams bot" is not a failure to fix and is not a success.
 *
 * A channel with no result at all reads as 'not attempted' rather than as a
 * failure: an older/newer server that omits a key must not render as broken.
 */
export function channelRow(result) {
  if (!result) return { tone: 'muted', label: 'Not attempted' };
  if (result.error) return { tone: 'bad', label: result.error };
  if (result.dryRun) return { tone: 'warn', label: 'Dry run — not sent' };
  if (result.skipped) return { tone: 'muted', label: `Skipped — ${result.reason || 'not attempted'}` };
  if (result.sent) return { tone: 'good', label: result.id ? `Sent (${result.id})` : 'Sent' };
  return { tone: 'muted', label: 'No result' };
}

/**
 * "your account email" vs "an override you typed" — an operator who overrode
 * one field and left the other blank should not have to work out where the
 * other message went.
 */
export function sourceNote(source) {
  if (source === 'override') return 'override';
  if (source === 'account') return 'your account';
  return null;
}

/**
 * The app settings that are missing across all three transports, deduplicated
 * and in a stable order. ACS_CONNECTION_STRING is shared by email and SMS, so
 * listing it twice would read as two separate problems.
 *
 * Returns [] when `config` is absent — an older server, which is not the same
 * as "nothing is missing", so callers should render this only when config came
 * back at all.
 */
export function missingSettings(config) {
  if (!config) return [];
  const out = [];
  const add = (name) => { if (name && !out.includes(name)) out.push(name); };
  if (config.email && !config.email.configured) {
    if (!config.email.hasConnection) add('ACS_CONNECTION_STRING');
    if (!config.email.hasSender) add('ACS_SENDER_ADDRESS');
  }
  if (config.sms && !config.sms.configured) {
    if (!config.sms.hasConnection) add('ACS_CONNECTION_STRING');
    if (!config.sms.hasFromNumber) add('ACS_SMS_FROM_NUMBER');
  }
  if (config.teams && !config.teams.configured) {
    if (!config.teams.hasAppId) add('TEAMS_BOT_APP_ID');
    if (!config.teams.hasPassword) add('TEAMS_BOT_APP_PASSWORD');
  }
  return out;
}

/**
 * Channels the RECIPIENT has switched off. The transport test says nothing
 * about these — a green send into a switched-off channel still reaches nobody —
 * so they are surfaced separately from the send results.
 *
 * SMS is excluded: it is opt-IN platform-wide (`sms: !!(sms && phone)`), so an
 * `sms: false` overwhelmingly means "never asked for texts", which is not a
 * finding. Reporting it would put a permanent amber line on most accounts.
 */
export function disabledChannels(prefs) {
  if (!prefs) return [];
  return [
    prefs.email === false && 'email',
    prefs.teams === false && 'Teams',
    prefs.push === false && 'push',
  ].filter(Boolean);
}

// ── The queue leg (GET /api/notifications/queues) ────────────────────────────
//
// Same split as above: the server names what is true and this file only decides
// how it is coloured and how each queue row reads. The panel exists because the
// send test above cannot see this leg at all — it tests the transport, and most
// notifications never reach the transport, they are enqueued and delivered by a
// worker later.

/**
 * Colour key per queue verdict kind.
 *
 * 'never-used' is 'warn' rather than 'good' on purpose: an absent queue reads as
 * quiet and means the opposite — the producer creates its queue on first send,
 * so nothing has ever gone through it. That is the state this panel was built
 * to make visible, and colouring it green would hide it.
 */
export const QUEUE_TONE = {
  idle: 'good',
  'partly-used': 'warn',
  'never-used': 'warn',
  backlog: 'warn',
  poison: 'bad',
  'no-storage': 'bad',
  unreadable: 'bad',
};

export function queueTone(kind) {
  return QUEUE_TONE[kind] || DEFAULT_TONE;
}

/**
 * One queue's row, as label + tone. FOUR states, and the distinction that
 * matters is between the two quiet ones: an EMPTY queue means the worker drained
 * it, an ABSENT queue means nothing was ever put in it. Rendering both as "0
 * messages" would report a feature that has never once run as a healthy one.
 */
export function queueRowLine(row) {
  if (!row) return { tone: 'muted', label: 'Not checked' };
  if (row.readable === false) return { tone: 'bad', label: row.error || 'Could not be read' };
  if (!row.exists) return { tone: 'warn', label: 'Queue does not exist — nothing has ever been enqueued' };
  if (row.poisonDepth > 0) {
    return {
      tone: 'bad',
      label: `${row.poisonDepth} dead-lettered — delivered to nobody`
           + (row.depth ? `, ${row.depth} waiting` : ''),
    };
  }
  if (row.depth > 0) return { tone: 'warn', label: `${row.depth} waiting to be delivered` };
  return { tone: 'good', label: 'Empty — everything enqueued has been delivered' };
}
